import React, { useState, useEffect } from 'react';
import { Modal, Input, Select } from 'antd';
import { toast } from 'react-toastify';
import { CheckCircle, Lock, FileCheck, Shield, User, Key, X, ShieldCheck } from 'lucide-react';
import Axios from 'axios';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  return `${day}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const DualESignatureAuth = ({
  visible,
  onCancel,
  onSubmit,
  signatureType = 'BMR_APPROVAL',
  signatureMeaning = 'Approval',
  documentType = 'BMR',
  batch_number = null,
  action_type = null,
  recipe_id = null,
  operationId = null,
  documentId = null,
  documentNumber = null,
  documentData = {},
  config = {
    enableBasic: true,
    enableCFR: true,
    defaultMode: 'cfr',
    requireComment: true,
    title: 'E-Signature Authentication',
  },
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [activeMode, setActiveMode] = useState(config.defaultMode || 'cfr');
  const [certificates, setCertificates] = useState([]);
  const [loadingCerts, setLoadingCerts] = useState(false);
  const [values, setValues] = useState({
    username: '',
    password: '',
    comment: '',
    certificate_id: null,
    certificate_password: '',
  });

  const setField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (verificationError) setVerificationError(null);
  };

  const resetForm = () => {
    setValues({ username: '', password: '', comment: '', certificate_id: null, certificate_password: '' });
    setVerificationError(null);
  };

  useEffect(() => {
    if (visible) {
      resetForm();
      setActiveMode(config.defaultMode || 'cfr');
      loadUserCertificates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const loadUserCertificates = async () => {
    try {
      setLoadingCerts(true);
      const token = localStorage.getItem('Token');
      const response = await Axios.get(`/bp/get_user_certificates`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.data.status === 'success') {
        setCertificates(response.data.data || []);
      } else {
        setCertificates([]);
      }
    } catch {
      setCertificates([]);
    } finally {
      setLoadingCerts(false);
    }
  };

  const buildSignatureData = (extra = {}) => {
    const token = localStorage.getItem('Token');
    const plantId = localStorage.getItem('selectedPlant') || localStorage.getItem('active_plant_id');
    const docData = {};
    if (batch_number) docData.batch_number = batch_number;
    if (operationId) docData.operation_id = operationId;
    if (recipe_id) docData.recipe_id = recipe_id;
    if (documentData && Object.keys(documentData).length > 0) {
      Object.keys(documentData).forEach((k) => {
        if (documentData[k] !== null && documentData[k] !== undefined && documentData[k] !== '')
          docData[k] = documentData[k];
      });
    }
    const base = {
      signature_type_code: action_type || signatureType,
      signature_meaning: signatureMeaning,
      reason: values.comment,
      document_type: documentType,
      document_id: documentId || batch_number || '',
      document_number: documentNumber || batch_number || '',
      document_version: '1.0',
      document_data: docData,
    };
    if (batch_number) base.batch_number = batch_number;
    if (action_type) base.action_type = action_type;
    if (recipe_id) base.recipe_id = recipe_id;
    if (operationId) { base.operation_id = operationId; if (documentType === 'OPERATION') base.document_id = operationId; }
    if (plantId) base.plant_id = plantId;
    return { data: { ...base, ...extra }, token };
  };

  const handleBasicAuth = async () => {
    const { username, password, comment } = values;
    if (!username || !password) { setVerificationError('Username and password are required.'); return; }
    if (config.requireComment && !comment) { setVerificationError('Remarks is required.'); return; }
    if (config.requireComment && comment.trim().length < 10) {
      setVerificationError('Remarks must be at least 10 characters.');
      toast.error('Remarks must be at least 10 characters.');
      return;
    }
    try {
      setIsVerifying(true);
      const { data, token } = buildSignatureData({ username, password });
      const response = await Axios.post(`/bp/create_esignature`, data, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setIsVerifying(false);
      if (response.data.status === 'success') {
        toast.success('E-signature created successfully');
        onSubmit({ ...values, method: 'BASIC', verified: true, signature_data: response.data.data });
        resetForm();
      } else {
        setVerificationError(response.data.message || 'Invalid username or password');
        toast.error(response.data.message || 'Invalid credentials');
      }
    } catch (err) {
      setIsVerifying(false);
      setVerificationError(err.response?.data?.message || 'Authentication failed. Please try again.');
      toast.error('Authentication failed');
    }
  };

  const handle21CFRAuth = async () => {
    if (!values.certificate_id) { setVerificationError('Please select a certificate.'); return; }
    if (config.requireComment && !values.comment) { setVerificationError('Remarks is required.'); return; }
    if (config.requireComment && values.comment.trim().length < 10) {
      setVerificationError('Remarks must be at least 10 characters.');
      toast.error('Remarks must be at least 10 characters.');
      return;
    }
    try {
      setIsVerifying(true);
      setVerificationError(null);
      const { data, token } = buildSignatureData({
        certificate_id: values.certificate_id,
        certificate_password: values.certificate_password || '',
      });
      const response = await Axios.post(`/bp/create_esignature_with_certificate`, data, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setIsVerifying(false);
      if (response.data.status === 'success') {
        toast.success('E-signature created successfully using certificate');
        onSubmit({ method: '21CFR', verified: true, comment: values.comment, certificate_verified: true, signature_data: response.data.data });
        resetForm();
      } else {
        setVerificationError(response.data.message || 'Certificate verification failed');
        toast.error(response.data.message || 'Certificate verification failed');
      }
    } catch (err) {
      setIsVerifying(false);
      setVerificationError(err.response?.data?.message || 'Certificate verification error. Please try again.');
      toast.error('Certificate verification error');
    }
  };

  const handleCancel = () => { resetForm(); onCancel(); };

  const showBasic = config.enableBasic !== false;
  const showCFR = config.enableCFR !== false;
  const bothEnabled = showBasic && showCFR;
  const commentInvalid = values.comment.length > 0 && values.comment.trim().length < 10;

  return (
    <Modal
      open={visible}
      onCancel={!isVerifying ? handleCancel : undefined}
      footer={null}
      centered
      width={500}
      closable={false}
      destroyOnHidden
      styles={{
        content: { padding: 0, overflow: 'hidden', borderRadius: '20px' },
        mask: { backdropFilter: 'blur(4px)' },
      }}
    >
      {/* ── Header ── */}
      <div className="bg-[#204448] px-8 pt-7 pb-6 relative">
        <button
          onClick={handleCancel}
          disabled={isVerifying}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-all"
        >
          <X size={15} />
        </button>

        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={28} className="text-green-400" />
          </div>
          <div>
            <h2 className="text-white text-lg font-bold m-0 leading-tight">
              {config.title || 'E-Signature Authentication'}
            </h2>
            {(batch_number || action_type) && (
              <p className="text-white/50 text-xs mt-0.5 m-0">
                {batch_number && <>Batch <span className="text-white/80 font-semibold">{batch_number}</span></>}
                {batch_number && action_type && ' · '}
                {action_type && <span className="text-white/80 font-semibold">{action_type}</span>}
                {operationId && <> · Op <span className="text-white/80 font-semibold">{operationId}</span></>}
              </p>
            )}
          </div>
        </div>

        {/* Mode switcher — only when both are enabled */}
        {bothEnabled && (
          <div className="mt-5 flex bg-white/10 rounded-xl p-1 gap-1">
            <button
              onClick={() => { setActiveMode('cfr'); setVerificationError(null); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                activeMode === 'cfr' ? 'bg-white text-[#204448] shadow-sm' : 'text-white/70 hover:text-white'
              }`}
            >
              <Shield size={13} />
              21 CFR Part 11
            </button>
            <button
              onClick={() => { setActiveMode('basic'); setVerificationError(null); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                activeMode === 'basic' ? 'bg-white text-[#204448] shadow-sm' : 'text-white/70 hover:text-white'
              }`}
            >
              <User size={13} />
              Basic Auth
            </button>
          </div>
        )}

        {/* Single-mode badge */}
        {!bothEnabled && (
          <div className="mt-5 flex bg-white/10 rounded-xl p-1">
            <div className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg bg-white text-[#204448] shadow-sm text-xs font-semibold">
              {showCFR ? <><Shield size={13} /> 21 CFR Part 11</> : <><User size={13} /> Basic Auth</>}
            </div>
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div className="bg-white px-8 py-6">
        {/* Error banner */}
        {verificationError && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-5">
            <span className="text-red-500 text-base flex-shrink-0 mt-0.5">⚠️</span>
            <p className="text-red-700 text-sm m-0">{verificationError}</p>
          </div>
        )}

        {/* ── 21 CFR ── */}
        {activeMode === 'cfr' && showCFR && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-xl mb-1">
              <FileCheck size={15} className="text-blue-500 flex-shrink-0" />
              <p className="text-blue-700 text-xs m-0 leading-relaxed">
                Digital certificate authentication — compliant with FDA 21 CFR Part 11 for audit trails and electronic records.
              </p>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Select Certificate <span className="text-red-500">*</span>
              </label>
              <Select
                placeholder="Choose your digital certificate"
                loading={loadingCerts}
                size="large"
                value={values.certificate_id}
                onChange={(v) => setField('certificate_id', v)}
                disabled={isVerifying}
                className="w-full"
                getPopupContainer={(t) => t.parentElement}
                notFoundContent={
                  <div className="text-center py-4">
                    <Shield size={24} className="text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-400 text-xs m-0">No certificates found.</p>
                    <p className="text-gray-400 text-xs m-0">Upload one in User Configuration.</p>
                  </div>
                }
              >
                {certificates.map((cert) => (
                  <Select.Option key={cert.certificate_id} value={cert.certificate_id} label={cert.common_name}>
                    <div className="py-1">
                      <div className="font-semibold text-sm">{cert.common_name}</div>
                      <div className="text-xs text-gray-400">{cert.organization} · Expires {formatDate(cert.expiry_date)}</div>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Certificate Password <span className="text-gray-400 font-normal">(if encrypted)</span>
              </label>
              <Input.Password
                prefix={<Key size={14} className="text-gray-400" />}
                placeholder="Leave blank if not password-protected"
                value={values.certificate_password}
                onChange={(e) => setField('certificate_password', e.target.value)}
                disabled={isVerifying}
                size="large"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Remarks {config.requireComment && <span className="text-red-500">*</span>}
              </label>
              <Input.TextArea
                placeholder="Provide a reason for this action (min. 10 characters)"
                value={values.comment}
                onChange={(e) => setField('comment', e.target.value)}
                disabled={isVerifying}
                rows={3}
                maxLength={500}
                showCount
                status={commentInvalid ? 'error' : ''}
              />
              {commentInvalid && <p className="text-red-500 text-xs mt-1">Minimum 10 characters required.</p>}
            </div>

            {certificates.length === 0 && !loadingCerts && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <span className="text-amber-500 flex-shrink-0">⚠️</span>
                <p className="text-amber-700 text-xs m-0">
                  No certificate available. Go to <strong>User Configuration</strong> to upload your digital certificate.
                </p>
              </div>
            )}

            <button
              onClick={handle21CFRAuth}
              disabled={isVerifying || !values.certificate_id || certificates.length === 0 || (config.requireComment && (!values.comment || values.comment.trim().length < 10))}
              className="w-full py-3 rounded-xl bg-[#204448] hover:bg-[#1a3538] disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              {isVerifying ? (
                <><span className="animate-spin">⏳</span> Verifying…</>
              ) : (
                <><Shield size={15} /> Verify Certificate & Sign</>
              )}
            </button>
          </div>
        )}

        {/* ── Basic Auth ── */}
        {activeMode === 'basic' && showBasic && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Username <span className="text-red-500">*</span>
              </label>
              <Input
                prefix={<User size={14} className="text-gray-400" />}
                placeholder="Enter your username"
                value={values.username}
                onChange={(e) => setField('username', e.target.value)}
                disabled={isVerifying}
                size="large"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Password <span className="text-red-500">*</span>
              </label>
              <Input.Password
                prefix={<Lock size={14} className="text-gray-400" />}
                placeholder="Enter your password"
                value={values.password}
                onChange={(e) => setField('password', e.target.value)}
                disabled={isVerifying}
                size="large"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Remarks {config.requireComment && <span className="text-red-500">*</span>}
              </label>
              <Input.TextArea
                placeholder="Provide a reason for this action (min. 10 characters)"
                value={values.comment}
                onChange={(e) => setField('comment', e.target.value)}
                disabled={isVerifying}
                rows={3}
                maxLength={500}
                showCount
                status={commentInvalid ? 'error' : ''}
              />
              {commentInvalid && <p className="text-red-500 text-xs mt-1">Minimum 10 characters required.</p>}
            </div>

            <button
              onClick={handleBasicAuth}
              disabled={isVerifying || !values.username || !values.password || (config.requireComment && (!values.comment || values.comment.trim().length < 10))}
              className="w-full py-3 rounded-xl bg-[#204448] hover:bg-[#1a3538] disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2"
            >
              {isVerifying ? (
                <><span className="animate-spin">⏳</span> Verifying…</>
              ) : (
                <><CheckCircle size={15} /> Verify & Sign</>
              )}
            </button>
          </div>
        )}

        {/* Cancel */}
        <button
          onClick={handleCancel}
          disabled={isVerifying}
          className="w-full mt-3 py-2.5 rounded-xl border border-gray-200 text-gray-500 text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default DualESignatureAuth;
